// bluaIQ — Diagnostic Takeoff v3 — DiagnosticBlockerPanel
// Read-only listing of blockers / confidence / no-silent-drop registries
// for a project. Reads the blocker_registry / confidence_registry /
// no_silent_drop artifacts and renders them as a small dashboard.
// Never displays quantities.

import { AlertTriangle, Gauge, ListChecks } from 'lucide-react'
import type { DiagnosticTakeoffArtifactManifest } from '../../takeoff-diagnostic-v3/types'

export interface DiagnosticBlockerPanelProps {
  artifacts: DiagnosticTakeoffArtifactManifest[]
}

function countByKind(artifacts: DiagnosticTakeoffArtifactManifest[], kind: string): number {
  return artifacts.filter((a) => a.kind === kind).length
}

export default function DiagnosticBlockerPanel({ artifacts }: DiagnosticBlockerPanelProps) {
  const blockerArtifacts = countByKind(artifacts, 'blocker_registry')
  const confidenceArtifacts = countByKind(artifacts, 'confidence_registry')
  const noSilentDropArtifacts = countByKind(artifacts, 'no_silent_drop')
  const silentDropSuspect = artifacts.some((a) => !a.noSilentDropOk)

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
        Blocker / confidence / no-silent-drop
      </div>
      <div className="grid grid-cols-3 gap-2">
        <PanelStat
          icon={<AlertTriangle size={14} />}
          label="Blocker registries"
          value={blockerArtifacts}
        />
        <PanelStat
          icon={<Gauge size={14} />}
          label="Confidence registries"
          value={confidenceArtifacts}
        />
        <PanelStat
          icon={<ListChecks size={14} />}
          label="No-silent-drop registries"
          value={noSilentDropArtifacts}
        />
      </div>
      <div className="text-[10px] font-[600] text-[#9E9890]">
        These are placeholder registries in the foundation milestone. They never
        gate a quantity, never feed Step B, and never feed Gate 3.
      </div>
      {silentDropSuspect && (
        <div className="text-[11px] font-[800] text-red-700 bg-red-50 border border-red-100 rounded-lg p-2">
          Silent-drop suspected on at least one artifact. Inspect the audit bundle.
        </div>
      )}
    </div>
  )
}

function PanelStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: number
}) {
  return (
    <div className="border border-[#ECEAE4] rounded-lg p-2.5 space-y-1 bg-[#FAFAF8]">
      <div className="text-[#9E9890]">{icon}</div>
      <div className="text-[18px] font-[900] text-[#1C1A17] leading-none">{value}</div>
      <div className="text-[9px] font-[700] text-[#9E9890]">{label}</div>
    </div>
  )
}
